import { useCallback, useEffect, useMemo, useState } from "react";

import useService from "../../../../../hooks/useService";
import { getModalHandler } from "../../../Common/Modals/hooks/useModalSingleton";
import STRINGS from "../../provider-management/utils/strings";
import {
  AVAILABLE_LIMIT,
  getServiceId,
  normalizeAvailableService,
  normalizeProvidedService,
  PROVIDED_LIMIT,
} from "../sections/services/servicesUtils";

export default function useProviderDetailServicesSection({
  permissionState,
  providerInformation,
}) {
  const servicesRepository = useService("repositories/service-repository");
  const providerId = providerInformation?.pk ?? providerInformation?.id;
  const [isVisible, setIsVisible] = useState(true);
  const [availableServices, setAvailableServices] = useState([]);
  const [providedServices, setProvidedServices] = useState([]);
  const [availableResultCount, setAvailableResultCount] = useState(0);
  const [providedResultCount, setProvidedResultCount] = useState(0);
  const [availableSearch, setAvailableSearch] = useState("");
  const [providedSearch, setProvidedSearch] = useState("");
  const [availablePage, setAvailablePage] = useState(1);
  const [providedPage, setProvidedPage] = useState(1);
  const [loadingState, setLoadingState] = useState({ available: false, provided: false, saving: false });
  const [selectedAvailableServices, setSelectedAvailableServices] = useState([]);
  const [selectedProvidedServices, setSelectedProvidedServices] = useState([]);
  const [servicesToBeAdded, setServicesToBeAdded] = useState([]);
  const [servicesToBeRemoved, setServicesToBeRemoved] = useState([]);

  const providedRemovedIds = useMemo(() => new Set(servicesToBeRemoved.map((item) => getServiceId(item))), [servicesToBeRemoved]);
  const availableAddedIds = useMemo(() => new Set(servicesToBeAdded.map((item) => getServiceId(item))), [servicesToBeAdded]);
  const providedServicesDisplay = useMemo(
    () => Array.from(new Map([...servicesToBeAdded, ...providedServices].filter((item) => !providedRemovedIds.has(getServiceId(item))).map((item) => [getServiceId(item), item])).values()),
    [providedRemovedIds, providedServices, servicesToBeAdded]
  );
  const availableServicesDisplay = useMemo(
    () => availableServices.filter((item) => !availableAddedIds.has(getServiceId(item))),
    [availableAddedIds, availableServices]
  );

  const refreshServices = useCallback(async () => {
    if (!providerId || !isVisible) {
      return;
    }
    try {
      if (permissionState.canManageProviderServices) {
        setLoadingState((prev) => ({ ...prev, available: true }));
      }
      setLoadingState((prev) => ({ ...prev, provided: true }));
      if (permissionState.canManageProviderServices) {
        const availableResponse = await servicesRepository.query({
          providerId,
          limit: AVAILABLE_LIMIT,
          offset: (availablePage - 1) * AVAILABLE_LIMIT,
          ...(availableSearch.trim() ? { search: availableSearch.trim() } : {}),
        }, true);
        setAvailableServices((availableResponse || []).map(normalizeAvailableService));
        setAvailableResultCount(await servicesRepository.getTotalCount());
      } else {
        setAvailableServices([]);
        setAvailableResultCount(0);
      }
      const providedResponse = await servicesRepository.query({
        providerId,
        limit: PROVIDED_LIMIT,
        offset: (providedPage - 1) * PROVIDED_LIMIT,
        ...(providedSearch.trim() ? { search: providedSearch.trim() } : {}),
      }, false);
      setProvidedServices((providedResponse || []).map(normalizeProvidedService));
      setProvidedResultCount(await servicesRepository.getTotalCount());
    } catch (error) {
      getModalHandler().openFailureModal(error);
    } finally {
      setLoadingState((prev) => ({ ...prev, available: false, provided: false }));
    }
  }, [availablePage, availableSearch, isVisible, permissionState.canManageProviderServices, providedPage, providedSearch, providerId, servicesRepository]);

  useEffect(() => {
    refreshServices();
  }, [refreshServices]);

  const resetSelection = () => {
    setSelectedAvailableServices([]);
    setSelectedProvidedServices([]);
    setServicesToBeAdded([]);
    setServicesToBeRemoved([]);
    setAvailableSearch("");
    setProvidedSearch("");
    setAvailablePage(1);
    setProvidedPage(1);
  };

  const moveRight = () => {
    const selectedIds = new Set(selectedAvailableServices.map((item) => getServiceId(item)));
    setServicesToBeAdded((prev) => [...prev, ...selectedAvailableServices]);
    setServicesToBeRemoved((prev) => prev.filter((item) => !selectedIds.has(getServiceId(item))));
    setSelectedAvailableServices([]);
  };

  const moveLeft = () => {
    const selectedIds = new Set(selectedProvidedServices.map((item) => getServiceId(item)));
    setServicesToBeRemoved((prev) => [...prev, ...selectedProvidedServices.filter((item) => item.serviceType !== STRINGS.SERVICE_TYPE.AVAILABLE)]);
    setServicesToBeAdded((prev) => prev.filter((item) => !selectedIds.has(getServiceId(item))));
    setSelectedProvidedServices([]);
  };

  const saveChanges = async () => {
    if (!providerId || (!servicesToBeAdded.length && !servicesToBeRemoved.length)) {
      return;
    }
    try {
      setLoadingState((prev) => ({ ...prev, saving: true }));
      if (servicesToBeAdded.length) {
        await servicesRepository.addRemoveService({ providerId }, true, servicesToBeAdded.map(getServiceId));
      }
      if (servicesToBeRemoved.length) {
        await servicesRepository.addRemoveService({ providerId }, false, servicesToBeRemoved.map(getServiceId));
      }
      getModalHandler().openSuccessModal();
      resetSelection();
      await refreshServices();
    } catch (error) {
      getModalHandler().openFailureModal(error);
    } finally {
      setLoadingState((prev) => ({ ...prev, saving: false }));
    }
  };

  return {
    AVAILABLE_LIMIT,
    PROVIDED_LIMIT,
    availablePage,
    availableResultCount,
    availableSearch,
    availableServicesDisplay,
    hasActiveSelection: selectedAvailableServices.length > 0 || selectedProvidedServices.length > 0,
    hasPendingChanges: servicesToBeAdded.length > 0 || servicesToBeRemoved.length > 0,
    isVisible,
    loadingState,
    providedPage,
    providedResultCount,
    providedSearch,
    providedServicesDisplay,
    selectedAvailableServices,
    selectedProvidedServices,
    setAvailablePage,
    setAvailableSearch,
    setIsVisible,
    setProvidedPage,
    setProvidedSearch,
    saveChanges,
    resetSelection,
    moveLeft,
    moveRight,
    setSelectedAvailableServices,
    setSelectedProvidedServices,
  };
}
